import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Input } from "../components/ui/input";
import { AREA_BY_ID, RESTAURANT_BY_ID } from "../constants";
import {
  clearSession,
  getCurrentUser,
  loadCategories,
  loadProducts,
  loadRequests,
  loadSuppliers,
  loadUsers,
} from "../lib/storage";
import { userAreas, visibleProducts } from "../lib/permissions";
import { apiGet } from "../lib/api";
import TodayPriorityRail from "../components/employee/TodayPriorityRail";
import EmployeeHeader from "../components/employee/EmployeeHeader";
import ProductCard from "../components/employee/ProductCard";
import RecentRequestsList from "../components/employee/RecentRequestsList";
import RequestSheet from "../components/employee/RequestSheet";
import { usePriorityProducts } from "../hooks/usePriorityProducts";
import { useEmployeeCart } from "../hooks/useEmployeeCart";
import { useLanguage } from "../i18n/LanguageContext";

export default function EmployeeApp() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchOpen, setSearchOpen] = useState(false);
  const [floatingQuery, setFloatingQuery] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [allRequests, setAllRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);

  useEffect(() => {
    const u = getCurrentUser();
    if (!u || u.role !== "employee") { navigate("/"); return; }
    setUser(u);
    setProducts(loadProducts());
    setCategories(loadCategories());
    setSuppliers(loadSuppliers());
    setAllRequests(loadRequests());
  }, [navigate]);

  const cart = useEmployeeCart(user, products, (next) => {
    setAllRequests(next);
    setSheetOpen(false);
  });

  const myOrders = useMemo(() => {
    if (!user) return [];
    const users = loadUsers();
    const name = (user.name || "").trim().toLocaleLowerCase();
    const sameName = users.filter((u) =>
      (u.name || "").trim().toLocaleLowerCase() === name && u.restaurant === user.restaurant
    );
    return (historyRequests || allRequests)
      .filter((r) => {
        if (r.type === "internal_transfer") return false;
        if (r.requestedBy === user.id) return true;
        // A recreated employee account has a new ID. Only link older orders
        // when no current user owns that ID and the name/location is unique.
        return name && sameName.length === 1 &&
          !users.some((u) => u.id === r.requestedBy) &&
          (r.requestedByName || "").trim().toLocaleLowerCase() === name &&
          r.restaurant === user.restaurant;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [user, allRequests, historyRequests]);

  const allowed = useMemo(() => visibleProducts(user, products), [user, products]);
  const supplierById = (id) => suppliers.find((s) => s.id === id);

  const priorityItems = usePriorityProducts(allowed, allRequests, user, 6);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const categoryList = activeCategory === "all" ? allowed : allowed.filter((p) => p.category === activeCategory);
    const list = !q ? categoryList : categoryList.filter(
      (p) => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q)
    );
    const groups = {};
    list.forEach((p) => {
      groups[p.category] = groups[p.category] || [];
      groups[p.category].push(p);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [allowed, query, activeCategory]);

  const floatingResults = useMemo(() => {
    const q = floatingQuery.trim().toLowerCase();
    if (!q) return [];
    return allowed.filter((p) => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
  }, [allowed, floatingQuery]);

  if (!user) return null;

  const uAreas = userAreas(user);
  const areaNames = uAreas.map((a) => AREA_BY_ID[a]?.name).filter(Boolean).join(" · ");
  const restaurant = RESTAURANT_BY_ID[user.restaurant];

  const handleSignOut = () => { clearSession(); navigate("/"); };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError(false);
    setHistoryRequests(null);
    try {
      const saved = await apiGet("/requests");
      if (!Array.isArray(saved)) throw new Error("Invalid request history");
      // Keep orders submitted on this device while its background sync finishes.
      const byId = new Map(saved.map((request) => [request.id, request]));
      loadRequests().forEach((request) => {
        if (!byId.has(request.id)) byId.set(request.id, request);
      });
      setHistoryRequests([...byId.values()]);
    } catch (error) {
      setHistoryError(true);
      setHistoryRequests(loadRequests());
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3EFE7] text-[#0B1F3A]">
      <EmployeeHeader
        user={user}
        cartCount={cart.totalCount}
        onSignOut={handleSignOut}
        onOpenHistory={openHistory}
        onOpenSheet={() => setSheetOpen(true)}
      />

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <Intro areaNames={areaNames} restaurant={restaurant} uAreas={uAreas} t={t} />

        <div className="sticky top-16 z-20 -mx-4 md:-mx-8 px-4 md:px-8 py-3 bg-[#F3EFE7]/95 backdrop-blur border-y border-[#E6E0D4] mb-5">
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <CategoryTab active={activeCategory === "all"} onClick={() => setActiveCategory("all")}>All products</CategoryTab>
            {categories.filter((c) => c.active !== false).map((category) => (
              <CategoryTab key={category.id} active={activeCategory === category.name} onClick={() => setActiveCategory(category.name)}>
                {category.name}
              </CategoryTab>
            ))}
          </div>
        </div>

        <div className="relative mb-5">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B1F3A]/40" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search")}
            className="pl-9 h-11 bg-white border-[#E6E0D4]"
          />
        </div>

        {!query && (
          <TodayPriorityRail items={priorityItems} supplierById={supplierById} onAdd={cart.inc} />
        )}

        {filtered.length === 0 ? (
          <div className="bg-white border border-[#E6E0D4] rounded-xl p-8 text-center">
            <p className="text-[#0B1F3A]/60">{t("noProducts")}</p>
          </div>
        ) : (
          filtered.map(([category, items]) => (
            <section key={category} className="mb-8">
              <h2 className="font-serif-display text-2xl mb-3">{category}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    qty={cart.cart[p.id]?.qty || 0}
                    supplier={supplierById(p.supplierId)}
                    onInc={cart.inc}
                    onDec={cart.dec}
                  />
                ))}
              </div>
            </section>
          ))
        )}

      </main>

      {historyOpen && (
        <div className="fixed inset-0 z-50 bg-[#0B1F3A]/40 backdrop-blur-sm p-3 sm:p-6" role="dialog" aria-modal="true" aria-label="My order history">
          <div className="max-w-2xl mx-auto h-full max-h-[92vh] bg-[#F3EFE7] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-[#E6E0D4] bg-white flex items-center">
              <div><p className="text-[10px] tracking-eyebrow uppercase text-[#0B1F3A]/55">My account</p><h2 className="font-serif-display text-2xl">Order history</h2></div>
              <div className="flex-1" />
              <button onClick={() => setHistoryOpen(false)} aria-label="Close order history" className="h-10 w-10 rounded-full hover:bg-[#F3EFE7] flex items-center justify-center"><X size={20} /></button>
            </div>
            <div className="overflow-y-auto px-4 sm:px-6 pb-6">
              {historyLoading ? (
                <p className="py-10 text-center text-sm text-[#0B1F3A]/55">Loading order history…</p>
              ) : (
                <>
                  {historyError && <p className="py-3 text-sm text-[#0B1F3A]/65">Offline: showing orders saved on this device.</p>}
                  <RecentRequestsList recent={myOrders} suppliers={suppliers} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        aria-label="Search products from anywhere"
        className="fixed z-40 right-5 bottom-6 h-14 w-14 rounded-full bg-[#0B1F3A] text-white shadow-[0_12px_30px_rgba(11,31,58,0.35)] flex items-center justify-center hover:bg-[#11294A]"
      >
        <Search size={23} />
      </button>

      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-[#0B1F3A]/45 backdrop-blur-sm p-3 sm:p-6" role="dialog" aria-modal="true" aria-label="Search products">
          <div className="max-w-2xl mx-auto h-full max-h-[92vh] bg-[#F3EFE7] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            <div className="p-4 bg-white border-b border-[#E6E0D4] flex items-center gap-3">
              <div className="relative flex-1">
                <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B1F3A]/45" />
                <Input autoFocus value={floatingQuery} onChange={(e) => setFloatingQuery(e.target.value)} placeholder={t("search")} className="pl-9 h-12 bg-[#F9F6EF] border-[#E6E0D4]" />
              </div>
              <button onClick={() => { setSearchOpen(false); setFloatingQuery(""); }} aria-label="Close search" className="h-10 w-10 rounded-full hover:bg-[#F3EFE7] flex items-center justify-center"><X size={20} /></button>
            </div>
            <div className="overflow-y-auto p-4 sm:p-6">
              {!floatingQuery.trim() ? (
                <p className="py-12 text-center text-sm text-[#0B1F3A]/55">Type a product or category name.</p>
              ) : floatingResults.length === 0 ? (
                <p className="py-12 text-center text-sm text-[#0B1F3A]/55">No matching products.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {floatingResults.map((product) => (
                    <ProductCard key={product.id} product={product} qty={cart.cart[product.id]?.qty || 0} supplier={supplierById(product.supplierId)} onInc={cart.inc} onDec={cart.dec} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <RequestSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        user={user}
        lines={cart.lines}
        notes={cart.notes}
        onNotes={cart.setNotes}
        onInc={cart.inc}
        onDec={cart.dec}
        onRemove={cart.remove}
        onLineNote={cart.setLineNote}
        onSubmit={cart.submit}
      />
    </div>
  );
}

function CategoryTab({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} className={`shrink-0 h-9 px-4 rounded-full border text-sm font-medium transition-colors ${active ? "bg-[#0B1F3A] border-[#0B1F3A] text-white" : "bg-white border-[#E6E0D4] text-[#0B1F3A] hover:border-[#0B1F3A]/40"}`}>
      {children}
    </button>
  );
}

function Intro({ areaNames, restaurant, uAreas, t }) {
  return (
    <div className="mb-6 fade-up">
      <p className="text-[11px] tracking-eyebrow uppercase text-[#0B1F3A]/55 mb-2">{t("missing")}</p>
      <h1 className="font-serif-display text-3xl md:text-5xl leading-tight">
        {t("question")} <span className="italic text-[#C4552B]">{t("restocked")}</span>?
      </h1>
      <p className="text-[#0B1F3A]/65 mt-3 max-w-xl text-sm md:text-base">
        {t("only")}{" "}
        <strong>{areaNames || t("yourAreas")}</strong> {t("at")} {restaurant?.name}.
      </p>
      {uAreas.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {uAreas.map((a) => (
            <span
              key={a}
              className="text-[10px] tracking-eyebrow uppercase bg-white border border-[#E6E0D4] rounded-full px-2.5 py-1 text-[#0B1F3A]/70"
            >
              {AREA_BY_ID[a]?.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
